import { useCallback, useEffect, useRef } from 'react';
import type { NavigatorImage } from './navigator/api';
import { isTail } from './navigator/sections';

/**
 * Which image to mark as seen now that a series is on screen (decided
 * 2026-09-25: an image is seen once its viewer is up, in one pane or any of
 * the compare panes): its public id, or null when there is nothing to send:
 * the reader's marks are not kept (a share link), the series is not one of
 * the navigator's (the tail has no id to mark), or it is marked already.
 */
export function imageToMark(byUid: ReadonlyMap<string, NavigatorImage>, uid: string, tracked: boolean): string | null {
  if (!tracked) return null;
  const img = byUid.get(uid);
  if (!img || isTail(img) || img.seen_utc) return null;
  return img.image_id;
}

/**
 * The series on screen, marked again right after a reset (user, 2026-09-27:
 * the image you are on counts): every compare pane's, or the single view's
 * once its viewer is up.
 */
export function onScreenSeries(compare: boolean, panes: (string | undefined)[], single: string | undefined, singleReady: boolean): string[] {
  if (compare) return panes.filter((uid): uid is string => !!uid);
  return singleReady && single ? [single] : [];
}

/**
 * When an image was seen, as its eye's tooltip says it (user, 2026-09-26): in
 * this computer's time zone, on a 12-hour clock. "Seen today, 3:42 PM" today,
 * "Seen Sep 26, 3:42 PM" earlier this year, the year added before that. The
 * app's words are English, so the clock is too (AM/PM, whatever the locale).
 * The formatters are made once: every seen card asks on every render.
 */
const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const DAY_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function seenAt(iso: string | null | undefined, now: Date = new Date()): string {
  const at = iso ? new Date(iso) : null;
  if (!at || Number.isNaN(at.getTime())) return 'Seen';
  const time = TIME.format(at);
  if (at.toDateString() === now.toDateString()) return `Seen today, ${time}`;
  return `Seen ${(at.getFullYear() === now.getFullYear() ? DAY : DAY_YEAR).format(at)}, ${time}`;
}

/**
 * The page's way to say "this series is on screen" (its viewer is up, in the
 * single view or a compare pane), marking it seen. A named image (?series=, a
 * share link, compare panes in the address) opens before the navigator has
 * answered, when there is nothing yet to say whether marks are kept or which
 * image the series is; such a series waits here and is marked the moment the
 * navigator answers. Settled once: a series marked, already seen, or not the
 * navigator's is forgotten, so a reset never brings back what was on screen
 * before it. Another study starts with nothing waiting.
 */
export function useSeenMarker(
  byUid: ReadonlyMap<string, NavigatorImage>,
  tracked: boolean,
  markSeen: (imageId: string) => void,
  study: string | undefined,
): (uid: string) => void {
  const waiting = useRef(new Set<string>());
  const latest = useRef({ byUid, tracked, markSeen });
  latest.current = { byUid, tracked, markSeen };
  // Whether the series could be settled now: marked if it needs to be.
  const settle = useCallback((uid: string): boolean => {
    const { byUid, tracked, markSeen } = latest.current;
    if (!tracked || !byUid.has(uid)) return false;
    const id = imageToMark(byUid, uid, tracked);
    if (id) markSeen(id);
    return true;
  }, []);
  useEffect(() => {
    waiting.current.clear();
  }, [study]);
  useEffect(() => {
    for (const uid of [...waiting.current]) if (settle(uid)) waiting.current.delete(uid);
  }, [byUid, tracked, settle]);
  return useCallback(
    (uid: string) => {
      if (!settle(uid)) waiting.current.add(uid);
    },
    [settle],
  );
}
